/**
 * Setting up or joining a live game of klaverjassen.
 *
 * Unlike the other two, the table is decided here and not by who taps first:
 * four known players, in seat order, and the two pairs fall out of that. A
 * device that is not one of the four cannot get in at all, which is exactly
 * what "teams chosen up front" has to mean if it is to mean anything.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Panel, Screen, Segmented, Toggle } from '@/components/ui';
import Avatar from '@/components/Avatar';
import { type KlaverjasOptions } from '@shared/game-logic';
import { api, ApiError } from '@/lib/api';
import { ROOM_CODE_LENGTH, isValidRoomCode, normalizeRoomCode } from '@/live/roomCode';
import { useRules } from '@/store/rules';

interface Profile {
  id: string;
  name: string;
  emoji: string;
  avatar: string | null;
}

const SEAT_LABELS = ['Wij — 1', 'Zij — 1', 'Wij — 2', 'Zij — 2'];

export default function JoinKlaverjasLive() {
  const navigate = useNavigate();
  const params = useParams<{ code?: string }>();

  const [profiles, setProfiles] = useState<Profile[]>([]);
  /**
   * The four chairs. A profile id, the string `computer`, or empty.
   *
   * A sentinel rather than a second array: the seat order *is* the teams (first and
   * third against second and fourth), so whatever sits there has to be in the same
   * list or the pairing drifts from what you see.
   */
  const [seats, setSeats] = useState<Array<string | null>>([null, null, null, null]);
  const [me, setMe] = useState<string | null>(null);
  // Same memory as the scorekeeper's klaverjassen: one household, one variant.
  const remembered = useRules((state) => state.klaverjas);
  const rememberKlaverjas = useRules((state) => state.rememberKlaverjas);
  const [options, setOptions] = useState<KlaverjasOptions>(remembered);
  const [code, setCode] = useState(params.code ? normalizeRoomCode(params.code) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api<Profile[]>('/api/profiles')
      .then(setProfiles)
      .catch(() => setProfiles([]));
  }, []);

  const seated = seats.filter(Boolean).length;
  const full = seated === 4;
  const codeOk = isValidRoomCode(code);

  const profileOf = (id: string | null) => profiles.find((profile) => profile.id === id) ?? null;
  const BOT = 'computer';
  const bots = seats.filter((seat) => seat === BOT).length;

  /** Put a computer in the first free chair. */
  function placeBot() {
    const free = seats.indexOf(null);
    if (free === -1) return;
    const next = [...seats];
    next[free] = BOT;
    setSeats(next);
  }

  function place(profileId: string) {
    // Already at the table? Tapping again takes them out again.
    const existing = seats.indexOf(profileId);
    if (existing !== -1) {
      const next = [...seats];
      next[existing] = null;
      return setSeats(next);
    }
    const free = seats.indexOf(null);
    if (free === -1) return;
    const next = [...seats];
    next[free] = profileId;
    setSeats(next);
  }

  return (
    <Screen
      title="Klaverjassen live"
      back="/"
      subtitle="Twee tegen twee, ieder op zijn eigen telefoon"
    >
      {error && (
        <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
          {error}
        </p>
      )}

      <Panel title="Wie ben jij">
        {profiles.length === 0 ? (
          <p className="text-sm text-slate-400">
            Er zijn nog geen spelers. Maak ze eerst aan in het beheer.
          </p>
        ) : (
          <ul className="grid grid-cols-4 gap-3 sm:grid-cols-6">
            {profiles.map((profile) => (
              <li key={profile.id}>
                <button
                  type="button"
                  aria-pressed={me === profile.id}
                  onClick={() => setMe(profile.id)}
                  className="flex w-full flex-col items-center gap-1"
                >
                  <span
                    className={`rounded-full transition ${
                      me === profile.id
                        ? 'ring-2 ring-accent-400 ring-offset-2 ring-offset-base-800'
                        : 'opacity-70'
                    }`}
                  >
                    <Avatar player={profile} size={56} />
                  </span>
                  <span className="w-full truncate text-center text-xs text-slate-400">
                    {profile.name}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Meedoen met een potje">
        <input
          value={code}
          onChange={(event) =>
            setCode(normalizeRoomCode(event.target.value).slice(0, ROOM_CODE_LENGTH))
          }
          placeholder="Code"
          inputMode="text"
          autoCapitalize="characters"
          autoComplete="off"
          className="min-h-14 w-full rounded-xl border border-white/10 bg-base-900 px-3 text-center font-tabular text-2xl tracking-[0.4em] outline-none focus:border-accent-500"
        />
        <div className="mt-3">
          <Button
            variant="primary"
            full
            disabled={!codeOk || !me}
            onClick={() =>
              navigate(`/klaverjas/live/${normalizeRoomCode(code)}`, { state: { profileId: me } })
            }
          >
            {me ? 'Meedoen' : 'Kies eerst wie je bent'}
          </Button>
        </div>
      </Panel>

      <Panel title="Of zet zelf een tafel op">
        <p className="mb-3 text-sm text-slate-400">
          Tik vier spelers aan. De eerste en derde vormen een team, de tweede en vierde het andere —
          maten zitten tegenover elkaar.
        </p>

        <ol className="mb-3 space-y-2">
          {SEAT_LABELS.map((label, seat) => {
            const profile = profileOf(seats[seat]);
            return (
              <li
                key={seat}
                className="flex items-center gap-3 rounded-xl bg-base-700/40 px-3 py-2"
              >
                <span
                  className={`h-7 w-1.5 rounded-full ${seat % 2 === 0 ? 'bg-accent-500' : 'bg-slate-500'}`}
                  aria-hidden
                />
                <span className="w-14 text-xs text-slate-500">{label}</span>
                {seats[seat] === BOT ? (
                  <>
                    <span className="text-xl leading-none">🤖</span>
                    <span className="flex-1 truncate text-sm">
                      computerspeler
                      <span className="ml-2 text-xs text-slate-500">speelt zelf</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const next = [...seats];
                        next[seat] = null;
                        setSeats(next);
                      }}
                      className="text-xs text-slate-500 hover:text-white"
                    >
                      weg
                    </button>
                  </>
                ) : profile ? (
                  <>
                    <Avatar player={profile} size={28} />
                    <span className="flex-1 truncate text-sm">{profile.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const next = [...seats];
                        next[seat] = null;
                        setSeats(next);
                      }}
                      className="text-xs text-slate-500 hover:text-white"
                    >
                      weg
                    </button>
                  </>
                ) : (
                  <span className="flex-1 text-sm text-slate-600">leeg</span>
                )}
              </li>
            );
          })}
        </ol>

        {bots > 0 && (
          <p className="mb-3 text-sm text-slate-500">
            Een potje met een computerspeler telt niet mee in de ranglijst.
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          {profiles.map((profile) => (
            <button
              key={profile.id}
              type="button"
              onClick={() => place(profile.id)}
              className={`flex items-center gap-1.5 rounded-full px-2 py-1 text-sm ${
                seats.includes(profile.id) ? 'bg-accent-500/20 text-accent-200' : 'bg-base-700'
              }`}
            >
              <Avatar player={profile} size={20} />
              {profile.name}
            </button>
          ))}

          {/* Fills the next free chair, so you can play two against two with one
              person at the table — or watch a whole deal play itself. */}
          <button
            type="button"
            onClick={placeBot}
            disabled={full}
            className="flex items-center gap-1.5 rounded-full bg-base-700 px-2 py-1 text-sm disabled:opacity-40"
          >
            🤖 computerspeler
          </button>
        </div>

        <div className="mt-4 space-y-4">
          <Segmented
            value={options.variant}
            onChange={(variant) => setOptions({ ...options, variant })}
            options={[
              {
                value: 'rotterdams',
                label: 'Rotterdams',
                hint: 'Altijd troeven, ook over je maat.',
              },
              {
                value: 'amsterdams',
                label: 'Amsterdams',
                hint: 'Heeft je maat de slag, dan hoeft het niet.',
              },
            ]}
          />
          <Segmented
            value={options.trumpChoice}
            onChange={(trumpChoice) => setOptions({ ...options, trumpChoice })}
            options={[
              { value: 'verplicht', label: 'Troef verplicht kiezen' },
              { value: 'vrij', label: 'Passen mag' },
            ]}
          />
          <Segmented
            value={options.length.kind === 'deals' ? String(options.length.deals) : 'punten'}
            onChange={(value) =>
              setOptions({
                ...options,
                length:
                  value === 'punten'
                    ? { kind: 'points', target: 1500 }
                    : { kind: 'deals', deals: Number(value) },
              })
            }
            options={[
              { value: '8', label: '8 giften' },
              { value: '16', label: '16 giften' },
              { value: 'punten', label: 'Tot 1500 punten' },
            ]}
          />
          <Toggle
            checked={options.pitBonus}
            onChange={(pitBonus) => setOptions({ ...options, pitBonus })}
            label="Pit telt 100 extra"
          />

          {/*
            Only on the live screen. The scorekeeper has a box you type roem into,
            so there is nothing for this to change there — and an option that does
            nothing is worse than one that is missing.
          */}
          <Segmented
            value={options.roemClaim}
            onChange={(roemClaim) => setOptions({ ...options, roemClaim })}
            options={[
              {
                value: 'automatisch',
                label: 'Roem automatisch',
                hint: 'De app ziet roem en telt hem op.',
              },
              {
                value: 'zelf',
                label: 'Zelf kloppen',
                hint: 'Roem telt alleen als je hem opeist zolang de slag ligt.',
              },
            ]}
          />
        </div>

        <div className="mt-4">
          <Button
            full
            variant="primary"
            disabled={busy || !full || !me || !seats.includes(me)}
            onClick={() => {
              setBusy(true);
              setError(null);
              void api<{ code: string }>('/api/klaverjas/rooms', {
                method: 'POST',
                body: JSON.stringify({
                  seats: seats.map((seat) => (seat === BOT ? { bot: true } : { profileId: seat })),
                  options,
                }),
              })
                .then(({ code: created }) => {
                  rememberKlaverjas(options);
                  navigate(`/klaverjas/live/${created}`, { state: { profileId: me } });
                })
                .catch((err) =>
                  setError(err instanceof ApiError ? err.message : 'Kon geen tafel opzetten.')
                )
                .finally(() => setBusy(false));
            }}
          >
            {!full
              ? `Nog ${4 - seated} speler(s) aan tafel zetten`
              : !me || !seats.includes(me)
                ? 'Zet ook jezelf aan tafel'
                : 'Tafel opzetten'}
          </Button>
        </div>
      </Panel>
    </Screen>
  );
}
